import {
  CheckCircleIcon,
  UserPlusIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CreateGuard } from '@/components/guards/permission-guards';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { API_ENDPOINTS } from '@/config/api-endpoints';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { Role, UserCreateFormData } from '@/types/api-responses';
import { RESOURCES } from '@/utils/permissions';

import {
  getAccessibilityProps,
  getApiErrorMessage,
  getDefaultUserCreateValues,
  getFieldProps,
  getLoadingStates,
  getSuccessMessages,
  prepareCreateUserPayload,
  transformRolesForSelect,
  userValidationRules,
} from './shared/user-form-utils';

interface UserCreateSheetProps {
  onUserCreated?: () => Promise<void>;
}

const UserCreateSheet: React.FC<UserCreateSheetProps> = ({ onUserCreated }) => {
  const { hideSheet } = useGlobalState();
  const api = useEnhancedApi();
  const { showSuccess, showError } = useToast();

  // State management
  const [roles, setRoles] = useState<Role[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [selectedRoleInfo, setSelectedRoleInfo] = useState<Role | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const hasFetchedRolesRef = useRef(false);

  // Form setup with enhanced defaults
  const { handleSubmit, control, reset, watch } = useForm<UserCreateFormData>({
    defaultValues: getDefaultUserCreateValues(),
    mode: 'onChange', // Enable real-time validation
  });

  // Watch roleId to show role details
  const watchedRoleId = watch('roleId');

  // Update selected role info when roleId changes
  useEffect(() => {
    if (watchedRoleId) {
      const role = roles.find(r => r._id === watchedRoleId);
      setSelectedRoleInfo(role || null);
    } else {
      setSelectedRoleInfo(null);
    }
  }, [watchedRoleId, roles]);

  // Fetch roles on component mount with better error handling
  const fetchRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    setFormErrors([]);

    try {
      const response = await api.get<{ roles: Role[] }>(
        API_ENDPOINTS.RBAC.GET_ROLES
      );

      if (response.success && response.data?.roles) {
        const activeRoles = response.data.roles.filter(
          role => role.isActive !== false
        );
        setRoles(activeRoles);

        if (activeRoles.length === 0) {
          setFormErrors([
            'No active roles available. Please contact your administrator.',
          ]);
        }
      } else {
        const errorMessage = getApiErrorMessage(
          response.error,
          'Failed to fetch available roles'
        );
        setFormErrors([errorMessage]);
        showError(errorMessage);
      }
    } catch {
      const errorMessage = 'Unable to connect to server. Please try again.';
      setFormErrors([errorMessage]);
      showError(errorMessage);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [api, showError]);

  useEffect(() => {
    if (!hasFetchedRolesRef.current) {
      hasFetchedRolesRef.current = true;
      fetchRoles();
    }
  }, [fetchRoles]);

  // Enhanced form submission with better error handling
  const handleCreateUser = handleSubmit(
    async (formData: UserCreateFormData) => {
      setIsSubmitting(true);
      setFormErrors([]);

      try {
        const payload = prepareCreateUserPayload(formData);
        const response = await api.post(API_ENDPOINTS.AUTH.INVITE, payload);

        if (response.success) {
          const displayName =
            formData.fullname || formData.username || formData.email;
          const successMessage = getSuccessMessages.userCreated(displayName);
          showSuccess(successMessage);
          reset();
          hideSheet('add-user');

          // Refresh the users list
          if (onUserCreated) {
            await onUserCreated();
          }
        } else {
          const errorMessage = getApiErrorMessage(
            response.error,
            'Failed to create user account'
          );
          setFormErrors([errorMessage]);
          showError(errorMessage);
        }
      } catch {
        const errorMessage =
          'Unable to create user. Please check your connection and try again.';
        setFormErrors([errorMessage]);
        showError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  );

  const handleCancel = useCallback(() => {
    reset();
    setFormErrors([]);
    setSelectedRoleInfo(null);
    hideSheet('add-user');
  }, [reset, hideSheet]);

  const handleRetryRoles = useCallback(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Expose handlers for dynamic button system
  React.useEffect(() => {
    const loadingStates = getLoadingStates(isSubmitting, isLoadingRoles);

    (
      window as typeof window & {
        userCreateHandlers?: {
          handleCreateUser: () => void;
          handleCancel: () => void;
          isLoading: boolean;
        };
      }
    ).userCreateHandlers = {
      handleCreateUser,
      handleCancel,
      isLoading: loadingStates.isSubmitDisabled,
    };

    return () => {
      delete (window as typeof window & { userCreateHandlers?: unknown })
        .userCreateHandlers;
    };
  }, [handleCreateUser, handleCancel, isSubmitting, isLoadingRoles]);

  // Get loading states
  const loadingStates = getLoadingStates(isSubmitting, isLoadingRoles);

  // Show error state if roles failed to load
  if (formErrors.length > 0 && roles.length === 0) {
    return (
      <div className='space-y-4 p-4'>
        <div className='flex items-center gap-3 p-4 bg-destructive-5 border border-destructive-20 rounded-lg'>
          <WarningIcon className='h-5 w-5 text-destructive-50 flex-shrink-0' />
          <div className='flex-1'>
            <h3 className='font-medium text-destructive-90'>
              Unable to Load Roles
            </h3>
            <div className='mt-1 text-sm text-destructive-70'>
              {formErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </div>
        </div>
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={handleRetryRoles}
            disabled={isLoadingRoles}
            className='px-4 py-2 text-sm font-medium text-destructive-70 bg-white border border-destructive-30 rounded-md hover:bg-destructive-5 disabled:opacity-50'
          >
            {isLoadingRoles ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <CreateGuard resource={RESOURCES.USERS}>
      <div className='space-y-6'>
        {/* Form errors display */}
        {formErrors.length > 0 && (
          <div className='p-4 bg-destructive-5 border border-destructive-20 rounded-lg'>
            <div className='flex items-start gap-3'>
              <WarningIcon className='h-5 w-5 text-destructive-50 flex-shrink-0 mt-0.5' />
              <div className='flex-1'>
                <h3 className='font-medium text-destructive-90 text-sm'>
                  Please fix the following errors:
                </h3>
                <ul className='mt-1 text-sm text-destructive-70 space-y-1'>
                  {formErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleCreateUser} className='space-y-4'>
          {/* Email field */}
          <Controller
            name='email'
            control={control}
            rules={userValidationRules.email}
            render={({ field, fieldState }) => {
              const fieldProps = getFieldProps(
                field,
                fieldState,
                loadingStates.isFormDisabled
              );
              const accessibilityProps = getAccessibilityProps(
                'email',
                !!fieldState?.error
              );

              return (
                <Input
                  {...fieldProps}
                  {...accessibilityProps}
                  label='Email Address'
                  type='email'
                  placeholder='user@company.com'
                  className='w-full'
                  autoComplete='email'
                  aria-describedby={
                    fieldState?.error ? 'email-error email-help' : 'email-help'
                  }
                />
              );
            }}
          />

          {/* Full name field (replaces username) */}
          <Controller
            name='fullname'
            control={control}
            rules={{
              ...userValidationRules.fullname,
              required: 'Full name is required',
            }}
            render={({ field, fieldState }) => {
              const fieldProps = getFieldProps(
                field,
                fieldState,
                loadingStates.isFormDisabled
              );
              const accessibilityProps = getAccessibilityProps(
                'fullname',
                !!fieldState?.error
              );

              return (
                <Input
                  {...fieldProps}
                  {...accessibilityProps}
                  label='Full Name'
                  placeholder='John Doe'
                  className='w-full'
                  autoComplete='name'
                  aria-describedby={
                    fieldState?.error
                      ? 'fullname-error fullname-help'
                      : 'fullname-help'
                  }
                />
              );
            }}
          />

          {/* Role selection field */}
          <Controller
            name='roleId'
            control={control}
            rules={userValidationRules.roleId}
            render={({ field, fieldState }) => {
              const accessibilityProps = getAccessibilityProps(
                'roleId',
                !!fieldState?.error
              );

              return (
                <div className='space-y-2'>
                  <FilterSelect
                    {...accessibilityProps}
                    name='roleId'
                    options={transformRolesForSelect(roles)}
                    className='w-full'
                    label='Role'
                    error={fieldState?.error?.message}
                    onChange={field.onChange}
                    value={field.value}
                    placeholder='Select a role for this user'
                    disabled={
                      loadingStates.isFormDisabled || roles.length === 0
                    }
                  />

                  {/* Show role information when selected */}
                  {selectedRoleInfo && (
                    <Card className='p-3 bg-brand-5 border-brand-20'>
                      <div className='flex items-start gap-3'>
                        <UserPlusIcon className='h-4 w-4 text-brand-60 mt-0.5 flex-shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className='font-medium text-brand-90 text-sm'>
                              {selectedRoleInfo.name}
                            </span>
                            <Badge variant='outline' className='text-xs'>
                              {selectedRoleInfo.permissions?.length || 0}{' '}
                              permissions
                            </Badge>
                          </div>
                          {selectedRoleInfo.description && (
                            <p className='text-xs text-brand-70 mt-1'>
                              {selectedRoleInfo.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              );
            }}
          />

          {/* Form status and submission info */}
          <div className='pt-4 border-t border-gray-20'>
            <div className='flex items-start gap-3 p-3 bg-gray-5 rounded-lg'>
              <CheckCircleIcon className='h-4 w-4 text-gray-50 mt-0.5 flex-shrink-0' />
              <div className='text-sm text-gray-60'>
                <p className='font-medium'>What happens next:</p>
                <ul className='mt-1 space-y-1 text-xs'>
                  <li>• The user will receive an email invitation</li>
                  <li>• They can set their password using the secure link</li>
                  <li>• Access will be granted based on the selected role</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
      </div>
    </CreateGuard>
  );
};

export default UserCreateSheet;
